import { formatName } from "../../../../../utils/format-name.ts";
import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { ClipboardList, UserRound } from "lucide-react";
import {
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  Modal,
  Spinner,
  TextArea,
} from "../../../../ui/index.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { useEmployeeProfile } from "../../../../../hooks/use-employee-profile.ts";
import { useOffboardingDetail } from "../../hooks/use-offboarding-detail.ts";
import { useOffboardingApproval } from "../../hooks/use-offboarding-approval.ts";
import { useOffboardingQuestionnaire } from "../../hooks/use-offboarding-questionnaire.ts";
import { displayFormatDate } from "../../../../../../utils/helpers.js";
import styles from "./coo-action-modal.module.css";

export interface CooActionModalProps {
  open: boolean;
  onClose: () => void;
  offboardingId: number;
  onSuccess: () => void;
}

type ActionMode = "approve" | "reject" | null;

function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseLocalIsoDate(value: string | null | undefined): Date | null {
  if (value == null || value.trim() === "") return null;

  const [year, month, day] = value.split("T")[0].split("-").map(Number);
  if ([year, month, day].some((part) => Number.isNaN(part))) return null;

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTentativeLwd(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "Not available";

  const formatted = displayFormatDate(value);
  return formatted === "Invalid Date" ? value : formatted;
}

export function CooActionModal({
  open,
  onClose,
  offboardingId,
  onSuccess,
}: CooActionModalProps) {
  const [actionMode, setActionMode] = useState<ActionMode>(null);
  const [finalLwdDate, setFinalLwdDate] = useState<Date | null>(null);
  const [finalLwdError, setFinalLwdError] = useState<string | undefined>();
  const [remarks, setRemarks] = useState("");

  const {
    detail,
    loading: detailLoading,
    error: detailError,
  } = useOffboardingDetail(open ? offboardingId : null);
  const employeeId = detail?.offboarding.employeeId ?? null;
  const {
    personalDetail,
    loading: profileLoading,
    error: profileError,
  } = useEmployeeProfile(open ? employeeId : null);
  const {
    questions,
    loading: questionsLoading,
    error: questionsError,
  } = useOffboardingQuestionnaire(open ? offboardingId : null, "RESIGNATION");
  const {
    approve,
    reject,
    loading: approvalLoading,
    error: approvalError,
  } = useOffboardingApproval();

  const resignationAnswers = useMemo(() => {
    const answers = detail?.resignationAnswers ?? [];
    if (answers.length === 0) return [];

    const questionById = new Map(
      questions.map((question) => [question.questionId, question.question]),
    );

    return answers
      .filter((item) => item.answer.trim() !== "")
      .map((item) => ({
        questionId: item.questionId,
        label:
          questionById.get(item.questionId) ?? `Question ${item.questionId}`,
        answer: item.answer,
      }));
  }, [detail?.resignationAnswers, questions]);

  const hasAnswers = resignationAnswers.length > 0;
  const employeeName = personalDetail?.empName ? formatName(personalDetail.empName) : undefined;
  const expectedLwd = detail?.offboarding.expectedLwd;
  const tentativeLwdLabel = useMemo(
    () => formatTentativeLwd(expectedLwd),
    [expectedLwd],
  );

  useEffect(() => {
    if (!open) {
      setActionMode(null);
      setFinalLwdDate(null);
      setFinalLwdError(undefined);
      setRemarks("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || actionMode !== "approve" || finalLwdDate != null) return;

    const expectedLwdDate = parseLocalIsoDate(expectedLwd);
    if (expectedLwdDate != null) {
      setFinalLwdDate(expectedLwdDate);
      setFinalLwdError(undefined);
    }
  }, [actionMode, expectedLwd, finalLwdDate, open]);

  useEffect(() => {
    if (detailError) showError(detailError);
  }, [detailError]);

  useEffect(() => {
    if (profileError) showError(profileError);
  }, [profileError]);

  useEffect(() => {
    if (questionsError) showError(questionsError);
  }, [questionsError]);

  useEffect(() => {
    if (approvalError) showError(approvalError);
  }, [approvalError]);

  const handleConfirmApprove = async () => {
    if (finalLwdDate == null) {
      setFinalLwdError("Final last working day is required.");
      return;
    }

    setFinalLwdError(undefined);

    const ok = await approve(offboardingId, localIsoDate(finalLwdDate), remarks.trim());
    if (ok) {
      showSuccess("Offboarding request approved successfully.");
      onSuccess();
      onClose();
    }
  };

  const handleConfirmReject = async () => {
    const ok = await reject(offboardingId, remarks.trim());
    if (ok) {
      showSuccess("Offboarding request rejected successfully.");
      onSuccess();
      onClose();
    }
  };

  const isBusy =
    detailLoading ||
    profileLoading ||
    questionsLoading ||
    approvalLoading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Offboarding Request Action"
      size="lg"
      className={styles.modalPanel}
      bodyClassName={styles.modalBody}
      headerClassName={styles.modalHeader}
      footer={
        <div className={styles.footer}>
          <Button
            variant="light-grey"
            onClick={onClose}
            disabled={approvalLoading}
          >
            Cancel
          </Button>
          {actionMode === null && (
            <>
              <Button
                variant="success"
                onClick={() => setActionMode("approve")}
                disabled={isBusy}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                onClick={() => setActionMode("reject")}
                disabled={isBusy}
              >
                Reject
              </Button>
            </>
          )}
          {actionMode === "approve" && (
            <Button
              variant="success"
              onClick={handleConfirmApprove}
              disabled={approvalLoading}
            >
              {approvalLoading ? "Submitting..." : "Approve"}
            </Button>
          )}
          {actionMode === "reject" && (
            <Button
              variant="danger"
              onClick={handleConfirmReject}
              disabled={approvalLoading}
            >
              {approvalLoading ? "Submitting..." : "Reject"}
            </Button>
          )}
        </div>
      }
    >
      <div className={styles.body}>
        {(detailLoading || profileLoading || questionsLoading) && <Spinner />}

        {!detailLoading && !profileLoading && !questionsLoading && (
          <>
            <section
              className={styles.requestSummary}
              aria-label="Request summary"
            >
              <div className={styles.summaryIcon} aria-hidden>
                <UserRound size={18} strokeWidth={2} />
              </div>
              <div className={styles.summaryText}>
                <h3 className={clsx("heading-sm", styles.employeeName)}>
                  {employeeName ?? "Employee Offboarding request"}
                </h3>
              </div>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4 className={clsx("sub-heading-lg", styles.sectionTitle)}>
                  Offboarding Details
                </h4>
              </div>
              {!hasAnswers && (
                <div className={styles.emptyState}>
                  <ClipboardList size={20} strokeWidth={2} aria-hidden />
                  <p className="body-md">No Records available</p>
                </div>
              )}
              {hasAnswers && (
                <dl className={styles.answerList}>
                  {resignationAnswers.map((item) => (
                    <div key={item.questionId} className={styles.answerItem}>
                      <dt className={clsx("sub-heading-md", styles.questionId)}>
                        {item.label}
                      </dt>
                      <dd className={clsx("body-md", styles.answerText)}>
                        {item.answer}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          </>
        )}

        {actionMode === "approve" && (
          <div className={styles.actionForm}>
            <div className={styles.tentativeLwdNote}>
              <p className={clsx("body-sm", styles.tentativeLwdText)}>
                <strong>Tentative Last Working Day: {tentativeLwdLabel}</strong>{" "}
                (calculated as per the employee&apos;s notice period). You may
                revise it with appropriate remarks, if required.
              </p>
            </div>
            <DatePicker
              id="final-lwd"
              label="Last Working Day"
              required
              disabled={approvalLoading}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select last working day"
              selected={finalLwdDate}
              minDate={new Date()}
              onChange={(date) => {
                setFinalLwdDate(date);
                if (finalLwdError) setFinalLwdError(undefined);
              }}
              customInput={<DatePickerFieldTrigger />}
              error={finalLwdError}
            />
            <TextArea
              id="approve-remarks"
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks"
              disabled={approvalLoading}
            />
          </div>
        )}

        {actionMode === "reject" && (
          <div className={styles.actionForm}>
            <TextArea
              id="reject-reason"
              label="Reason"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter reason"
              disabled={approvalLoading}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
